import { assignOrderDesigner, markOrderDesigned, markLocalOrderDesigned,
  createCachedOrderStatusMutation, getOrder, getLocalOrder } from '@/api/orders';
import { offlineEditContext, pendingBlocksDesign } from '@/utils/offlineEditContinuity';

// Renderer-lifetime custody, shared by both layouts and replacement screens.
// No credential, recipient, draft or complete order is retained here.
const claims = new Set();
const observers = new Set();
const emit = () => observers.forEach(fn => fn());
const localKind = kind => ['local', 'queued'].includes(kind);
const validOrder = item => typeof item?.id === 'string'
  && ['new', 'accepted', 'designed', 'on_route', 'delivered', 'failed'].includes(item.status);

function acknowledgement(value, claim) {
  if (claim.kind === 'assign') return validOrder(value) && value.id === claim.id
    && value.status === 'accepted' && (value.assigned_designer_id ?? null) === claim.designerId
    ? 'Assignment saved' : null;
  if (value?.success !== true || value.order_id !== claim.id || value.new_status !== 'designed'
    || value.previous_status !== 'accepted') return null;
  if (claim.kind === 'queued') return typeof value.mutation_id === 'string' && value.mutation_id.trim()
    && value.state === 'pending_sync' ? 'Completion queued for Review' : null;
  return claim.kind === 'local' ? 'Completed locally; awaiting synchronization' : 'Marked as designed';
}

function refusal(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if ([401, 403].includes(status)) return 'Permission unavailable. Sign in and review current access.';
  if ([400, 404, 409, 422].includes(status) && typeof detail === 'string' && (
    ['This record changed before your update completed. Refresh and try again.',
      'Cached order already has an offline change pending', 'Designer not found',
      'Cannot assign a disabled designer', 'Target user does not have the designer role',
      'Order not found', 'Unsynchronized local order not found', 'Cached cloud order not found',
      'Can only mark accepted local orders as designed'].includes(detail)
    || detail.startsWith('Cannot transition from ') || detail.startsWith('Cannot reassign order in status '))) {
    return 'The order or designer changed. Refresh and review before trying again.';
  }
  return null;
}

export function createDesignDecisions(capture, read) {
  let active = false;
  let authorityDenied = false;
  let snapshot = [];
  const listeners = new Set();
  const reviews = new Map();
  const ownerFor = claim => capture(localKind(claim.kind));
  const related = claim => {
    const owner = ownerFor(claim);
    return owner?.endpoint === claim.endpoint && owner.tenant === claim.tenant;
  };
  const find = id => [...claims].find(claim => claim.id === id && related(claim));
  const notify = () => {
    snapshot = [...claims].filter(related).map(claim => ({ id: claim.id, state: claim.state,
      stalled: claim.stalled, message: claim.message, review: reviews.get(claim)?.message,
      canAcknowledge: reviews.get(claim)?.ready === true }));
    listeners.forEach(fn => fn());
  };
  const eligible = (order, owner, kind, designerId) => validOrder(order) && order.status === 'accepted'
    && !(kind === 'queued' ? pendingBlocksDesign(order) : order.desktop_sync?.pending_mutation) && (kind !== 'assign' || (!owner.offline
      && order.desktop_sync?.origin !== 'local' && (owner.admin || (designerId === owner.actor
        ? !order.assigned_designer_id || order.assigned_designer_id === owner.actor
        : designerId === null && order.assigned_designer_id === owner.actor))));
  return {
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    getSnapshot: () => snapshot,
    activate() { active = true; observers.add(notify); notify(); },
    retire() { active = false; reviews.clear(); observers.delete(notify); },
    blocked: id => authorityDenied || !!find(id),
    async run(order, action, { designerId = null, current = () => true, onDispatch = () => {} } = {}) {
      const kind = action === 'assign' ? action : order?.desktop_sync?.origin === 'local'
        ? 'local' : capture(false)?.offline ? 'queued' : 'complete';
      const owner = capture(localKind(kind));
      if (!active || authorityDenied || !owner || !current() || find(order?.id)
        || !eligible(order, owner, kind, designerId)) return { state: 'blocked' };
      const claim = { endpoint: owner.endpoint, tenant: owner.tenant, id: order.id,
        kind, designerId, state: 'pending' };
      claims.add(claim); emit();
      const owns = () => active && owner.current();
      if (!owns() || !current()) { claims.delete(claim); emit(); return { state: 'retired' }; }
      const timer = setTimeout(() => { claim.stalled = true; emit(); }, 30000);
      let message; let refused;
      try {
        onDispatch();
        const result = kind === 'assign' ? await assignOrderDesigner(order.id, designerId, owner.config)
          : kind === 'local' ? await markLocalOrderDesigned(order.id, owner.config)
            : kind === 'queued' ? await createCachedOrderStatusMutation(order.id, 'designed', owner.config, offlineEditContext(order, 'admin'))
              : await markOrderDesigned(order.id, owner.config);
        message = acknowledgement(result, claim);
      } catch (error) {
        refused = refusal(error);
        if (owns() && [401, 403].includes(error?.response?.status)) authorityDenied = true;
      } finally {
        clearTimeout(timer);
        if (refused) claims.delete(claim);
        else { claim.state = message ? 'accepted' : 'unknown'; claim.message = message; }
        emit();
      }
      if (!owns()) return { state: 'retired' };
      return { state: message ? 'accepted' : refused ? 'refused' : 'unknown',
        message: message || refused || 'Result not confirmed. Check current order before trying again.' };
    },
    // Only a fresh successful list observation retires an acknowledged write.
    refreshed(ids) {
      if (!active) return;
      ids.forEach(id => {
        const claim = find(id);
        if (claim?.state === 'accepted' && ownerFor(claim)?.current()) claims.delete(claim);
      });
      emit();
    },
    async review(id) {
      const claim = find(id);
      if (!active || authorityDenied || claim?.state !== 'unknown') return;
      const owner = ownerFor(claim);
      if (!owner) return;
      const attempt = {};
      reviews.set(claim, attempt); notify();
      const owns = () => active && owner.current() && claims.has(claim) && reviews.get(claim) === attempt;
      try {
        const item = await read(config => {
          if (!owns()) throw new Error('Observation retired');
          return (localKind(claim.kind) ? getLocalOrder : getOrder)(id, config);
        }, owner.config);
        if (!owns()) return;
        if (!validOrder(item) || item.id !== id) throw new Error('Invalid observation');
        const pending = !!item.desktop_sync?.pending_mutation;
        const achieved = claim.kind === 'assign' ? (item.assigned_designer_id ?? null) === claim.designerId
          : ['designed', 'on_route', 'delivered'].includes(item.status);
        const retry = eligible(item, owner, claim.kind, claim.designerId);
        reviews.set(claim, { current: owner.current, ready: pending || achieved || retry || item.status !== 'accepted',
          message: pending ? 'An offline change is pending. Continue in Review; do not repeat it.'
            : achieved ? 'Requested state is now present. This does not establish which attempt saved it.'
              : retry ? 'Current order checked. A new attempt requires acknowledgement and a deliberate new action.'
                : 'Current state does not permit that action. Review the order before continuing.' });
      } catch (error) {
        if (!owns()) return;
        const absent = error?.response?.status === 404 && error.response.data?.detail === 'Order not found';
        reviews.set(claim, { current: owner.current, ready: absent,
          message: absent ? 'Order currently absent; no completion is established.' : 'Current order could not be verified. Retry remains blocked.' });
      }
      notify();
    },
    acknowledge(id) {
      const claim = find(id); const evidence = reviews.get(claim);
      if (!active || authorityDenied || claim?.state !== 'unknown' || !evidence?.ready
        || !evidence.current() || !ownerFor(claim)?.current()) return false;
      claims.delete(claim); reviews.delete(claim); emit();
      return true;
    },
  };
}
